import { scribeHttpV2 } from '@/utils/scribeHTTP';

export type CuaCloudProviderKind = 'running' | 'waking' | 'paused' | 'unknown';

export type CuaCloudMachineStatus = 'provisioning' | 'ready' | 'failed';

const WAKING_PROVIDER_STATES = new Set([
  'starting',
  'creating',
  'restoring',
  'queued',
  'pending',
  'pulling',
  'resuming',
  'building',
]);

const PAUSED_PROVIDER_STATES = new Set(['stopped', 'archived', 'paused']);

/** Classify Daytona's live sandbox state for status copy and the live view. */
export const classifyCuaCloudProviderState = (
  state: string | undefined
): CuaCloudProviderKind => {
  if (state === 'started') return 'running';
  if (state && WAKING_PROVIDER_STATES.has(state)) return 'waking';
  if (state && PAUSED_PROVIDER_STATES.has(state)) return 'paused';
  return 'unknown';
};

export interface CuaCloudMachine {
  machine_id: string;
  status: CuaCloudMachineStatus;
  created_at: string;
}

export interface CuaCloudMachineDetails extends CuaCloudMachine {
  display_name: string | null;
  snapshot_name: string;
  placement_target: string;
  /** Live Daytona sandbox state: started, stopped, etc. */
  provider_state: string;
  /** Pause after N idle minutes; 0 keeps the machine always on. */
  auto_stop_minutes: number;
}

/** PATCH semantics: only fields present in the body are changed. */
// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- Noun collision: "Settings" is the cloud VM's own settings payload (rename, auto-pause), not workspace/identity-projection data.
export interface CuaCloudSettingsUpdate {
  display_name?: string | null;
  auto_stop_minutes?: number;
}

// "Session" here is a noun collision with the VNC preview link, not Heidi
// session data — named "link" to keep it unambiguous.
export interface CuaCloudVncLink {
  url: string;
  expires_at: string;
}

// UNCARVED DOMAIN KEY (rpa, CUA-65): rpa has no workspace package
export const CUA_CLOUD_MACHINE_QUERY_KEY = ['rpa', 'cua-cloud', 'machine'] as const;

const TIMEOUT = 30_000;
// Provisioning / start-stop calls hit the Daytona control plane and can be slow.
const SLOW_TIMEOUT = 90_000;

export const cuaCloudClient = {
  ensureMachine: () =>
    scribeHttpV2.put<CuaCloudMachine>('/rpa/cua-cloud/machine', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
  getMachine: () =>
    scribeHttpV2.get<CuaCloudMachine>('/rpa/cua-cloud/machine', { timeout: TIMEOUT }),
  getMachineDetails: () =>
    scribeHttpV2.get<CuaCloudMachineDetails>('/rpa/cua-cloud/machine/details', {
      timeout: TIMEOUT,
    }),
  pauseMachine: () =>
    scribeHttpV2.post<CuaCloudMachine>('/rpa/cua-cloud/machine:pause', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
  resumeMachine: () =>
    scribeHttpV2.post<CuaCloudMachine>('/rpa/cua-cloud/machine:resume', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
  deleteMachine: () =>
    scribeHttpV2.delete<void>('/rpa/cua-cloud/machine', { timeout: SLOW_TIMEOUT }),
  updateSettings: (settings: CuaCloudSettingsUpdate) =>
    scribeHttpV2.patch<CuaCloudMachineDetails>(
      '/rpa/cua-cloud/machine/settings',
      settings,
      {
        timeout: TIMEOUT,
      }
    ),
  createVncLink: () =>
    scribeHttpV2.post<CuaCloudVncLink>('/rpa/cua-cloud/machine:vnc-session', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
};
